/*
 * Copyright (C) Serfim TIC
 * All rights reserved
 */

class DateTimeService {
    /** @ngInject */
    constructor($log, configuration, moment) {
        this.$log = $log;
        this.configuration = configuration;
        this.moment = moment;
        this.moment.locale(this.configuration.LANG);
        this.$log.debug('Service \'DateTimeService\' loaded');
    }

    /**
     * Parses a time and returns a Date instance with the given time (date is 1970-01-01).
     *
     * @param {String} timeStr String containing the time.
     * @param {String} format Time format (optional). If none is given, defaults to 'HH:mm'.
     * @return {Date} A date object with the given time, null if the time could not be parsed.
     */
    parseTime(timeStr, format) {
        const formatUsed = format || 'HH:mm';
        const moment = this.moment.utc(`1970-01-01 ${timeStr}`, `YYYY-MM-DD ${formatUsed}`);
        return moment.isValid() ? moment.toDate() : null;
    }

    /**
     * Extracts and format the time part of a Date instance.
     *
     * @param {Date} dateObj A date instance.
     * @param {String} format Time format (optional). If none is given, defaults to 'HH:mm'.
     * @return {String} A string containing the time in the date instance.
     */
    formatTime(dateObj, format) {
        return this.moment(dateObj).utc().format(format || 'HH:mm');
    }

    /**
     * Parses an UTC date and returns a Date instance (time is 00:00:00).
     *
     * @param {String} dateStr String containing the date.
     * @param {String} format Date format (optional). If none is given, defaults to 'YYYY-MM-DD'.
     * @return {Date} A date object with the given date, undefined if the date could not be parsed.
     */
    parseDate(dateStr, format) {
        const formatUsed = format || 'YYYY-MM-DD';
        const moment = this.moment.utc(`${dateStr}`, formatUsed);
        return moment.isValid() ? moment.toDate() : undefined;
    }

    /**
     * Parses a local date and returns a Date instance (time is 00:00:00).
     *
     * @param {String} dateStr String containing the date.
     * @param {String} format Date format (optional). If none is given, defaults to 'YYYY-MM-DD'.
     * @return {Date} A date object with the given date, undefined if the date could not be parsed.
     */
    parseLocalDate(dateStr, format) {
        const formatUsed = format || 'YYYY-MM-DD';
        const moment = this.moment(`${dateStr}`, formatUsed);
        return moment.isValid() ? moment.toDate() : undefined;
    }

    /**
     * Extracts and format the date part of a Date instance.
     *
     * @param {Date} dateObj A date instance.
     * @param {String} format Date format (optional). If none is given, defaults to 'YYYY-MM-DD'.
     * @return {String} A string containing the date in the date instance.
     */
    formatDate(dateObj, format) {
        return this.moment(dateObj).utc().format(format || 'YYYY-MM-DD');
    }

    /**
     * Extracts and format the date part of a Date instance as a local date.
     *
     * @param {Date} dateObj A date instance.
     * @param {String} format Date format (optional). If none is given, defaults to 'YYYY-MM-DD'.
     * @return {String} A string containing the date in the date instance.
     */
    formatLocalDate(dateObj, format) {
        return this.moment(dateObj).format(format || 'YYYY-MM-DD');
    }

    /**
     * Adds one ISO week to the given date string. Example:
     *
     * <pre>
     * incrementIsoweek('2017-S52', 'GGGG-[S]WW') // '2018-S01'
     * </pre>
     *
     * @param {String} dateStr String containing the date to parse.
     * @param {String} format Date format (optional). If none is given, defaults to 'YYYY-MM-DD'.
     * @return {String} A string containing the date incremented and formatted.
     */
    incrementIsoweek(dateStr, format) {
        const formatUsed = format || 'YYYY-MM-DD';
        return this.moment(`${dateStr}`, formatUsed).add(1, 'weeks').format(formatUsed);
    }

    /**
     * Gets the date of the first local day in the first week in the month. This may be a day in the previous month, if
     * the first week in the month is not entirely contained in this month. Example:
     *
     * <pre>
     * getFirstDayOfFirstWeekCurrentMonth(
     * // 2017-04-19 09:28:00
     * )
     * // 2017-03-27 00:00:00 is monday in week 13, which is the first week of the april month.
     * </pre>
     *
     * @param {Date} dateObj A date instance.
     * @return {Date} A new date instance.
     */
    getFirstDayOfFirstWeekCurrentMonth(dateObj) {
        const moment = this.moment(dateObj).date(1);
        if (moment.isoWeekday() !== 1) {
            moment.startOf('isoWeek');
        }
        return moment.toDate();
    }

    /**
     * Gets the date of the first day in the first week entirely contained in the next month. Example:
     *
     * <pre>
     * getFirstDayOfFirstPlainWeekNextMonth(
     * // 2017-05-19 09:28:00
     * )
     * // 2017-06-05 00:00:00 is monday in week 23, which is the first week entirely contained in the june month.
     * </pre>
     *
     * @param {Date} dateObj A date instance.
     * @return {Date} A new date instance.
     */
    getFirstDayOfFirstPlainWeekNextMonth(dateObj) {
        const moment = this.moment(dateObj);
        // Go to the first day of the next month.
        moment.month(moment.month() + 1).date(1).startOf('day');
        let day;
        if (moment.isoWeekday() === 1) {
            day = moment.toDate();
        } else {
            day = this.getFirstDayOfNextWeek(moment.toDate());
        }
        return day;
    }

    /**
     * Get the date of the first day of the year of the previous month.
     * ex:
     *  -2022-03-05 -> 2022-01-01
     *  -2022-01-28 -> 2021-01-01
     * @param dateObj
     * @returns {Date}
     */
    getFirstDayOfYearOfPreviousMonth(dateObj) {
        if (dateObj.getMonth()===0) {
            return new Date(dateObj.getFullYear()-1, 0, 1);
        } else {
            return new Date(dateObj.getFullYear(), 0, 1);
        }
    }

    /**
     * Get the date of the last day of the previous month.
     * @param dateObj
     * @returns {Date}
     */
    getLastDayOfPreviousMonth(dateObj) {
        let res =  dateObj;
        res.setDate(1);
        res.setHours(-1);
        return res;
    }

    /**
     * Gets the date of the first day at minight, the week after the given date object.
     *
     * @param {Date} dateObj A date instance.
     * @return {Date} A new date instance.
     */
    getFirstDayOfNextWeek(dateObj) {
        return this.moment(dateObj).add(1, 'weeks').isoWeekday(1).toDate();
    }

    /**
     * Gets the number of the first week in the month.
     *
     * @param {Number} year Year.
     * @param {Number} month Month.
     * @return {Date} A new date instance.
     */
    getFirstWeekInYearMonth(year, month) {
        return this.moment(new Date(year,month)).add(1, 'weeks').isoWeekday(1).toDate();
    }

    /**
     * Gets the planned week of a preventive sheet, from its max action date. The max action date is the limit date
     * where prevention shall be done. Technically, it is the first day of the ISO week following the planned week.
     * Then, the expected week of action being the previous ISO week, we simply substract one week to get this ISO week.
     *
     * @param {String} maxActionDate The preventive sheet's maximum action date as a 'YYYY-MM-DD' string.
     * @return {String} The planned week as a 'YYYY-[S]WW' string.
     */
    toPreventiveSheetPlannedWeek(maxActionDate) {
        const parsedDate = this.parseDate(maxActionDate);
        let plannedWeek;
        if (parsedDate) {
            parsedDate.setDate(parsedDate.getDate() - 7);
            plannedWeek = this.formatDate(parsedDate, this.configuration.WEEK_OF_YEAR_FORMAT);
        }
        return plannedWeek;
    }

    /**
     * Gets the max action date of a preventive sheet, from its planned week. The max action date is the limit date
     * where prevention shall be done. Technically, it is the first day of the ISO week following the planned weed.
     *
     * @param {String} plannedWeek The planned week as a 'YYYY-[S]WW' string.
     * @return {String} The preventive sheet's maximum action date as a 'YYYY-MM-DD' string.
     */
    toPreventiveSheetMaxActionDate(plannedWeek) {
        const parsedDate = this.parseDate(plannedWeek, this.configuration.WEEK_OF_YEAR_FORMAT);
        let maxActionDate;
        if (parsedDate) {
            parsedDate.setDate(parsedDate.getDate() + 7);
            maxActionDate = this.formatDate(parsedDate);
        }
        return maxActionDate;
    }

    /**
     * Gets the remaining months since the given date object with the given period.
     * If the remaining months is lower than 0, forced to 0.
     * If the remaining months is between 0 and 1, round to 2 decimals.
     * If the remaining months is greater than 1, ceil to next int.
     *
     * @param {Date} startDate The start date as date instance.
     * @return {Number} A number of remaining months.
     */
    getRemainingMonths(startDate, period) {
        let remainingMonths = period - this.moment().diff(this.moment(startDate), 'month', true);
        if (remainingMonths > 0) {
            if (remainingMonths < 1) {
                remainingMonths = Math.round(remainingMonths * 100) / 100;
            } else {
                remainingMonths = Math.ceil(remainingMonths);
            }
        } else {
            remainingMonths = 0;
        }
        return remainingMonths;
    }
}

export { DateTimeService };



// WEBPACK FOOTER //
// ./src/main/front/scripts/module/util/service/DateTimeService.js